/**
 * Request/Response Interceptors
 */

import type {
  RequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
  ErrorInterceptor,
} from './types';
import { ApiError } from './errors';
import { isDevelopment } from './config';

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    return localStorage.getItem('dc-copilot-token');
  } catch {
    return null;
  }
}

/**
 * Default request interceptor
 * Adds common headers, auth token, and logs requests in development
 */
export const defaultRequestInterceptor: RequestInterceptor = (config) => {
  // Get auth token from localStorage
  const token = getAuthToken();

  // Add default headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...config.headers,
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Log request in development
  if (isDevelopment) {
    console.log('[API Request]', {
      method: config.method || 'GET',
      url: config.body ? 'See body' : 'N/A',
      headers,
      hasToken: !!token,
    });
  }

  return {
    ...config,
    headers,
  };
};

/**
 * Default response interceptor
 * Handles response parsing and logging
 */
export const defaultResponseInterceptor: ResponseInterceptor = async (response) => {
  // Log response in development
  if (isDevelopment) {
    console.log('[API Response]', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
  }

  return response;
};

/**
 * Default error interceptor
 * Transforms errors into ApiError instances
 */
export const defaultErrorInterceptor: ErrorInterceptor = (error) => {
  if (error instanceof ApiError) {
    return error;
  }

  // Log error in development
  if (isDevelopment) {
    console.error('[API Error]', error);
  }

  return error;
};

/**
 * Interceptor manager
 */
export class InterceptorManager {
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.requestInterceptors.push(interceptor);
    // Return unsubscribe function
    return () => {
      const index = this.requestInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.requestInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.responseInterceptors.push(interceptor);
    // Return unsubscribe function
    return () => {
      const index = this.responseInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.responseInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * Add error interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): () => void {
    this.errorInterceptors.push(interceptor);
    // Return unsubscribe function
    return () => {
      const index = this.errorInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.errorInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * Execute all request interceptors
   */
  async executeRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
    let result = config;
    for (const interceptor of this.requestInterceptors) {
      result = await interceptor(result);
    }
    return result;
  }

  /**
   * Execute all response interceptors
   */
  async executeResponseInterceptors(response: Response): Promise<Response> {
    let result = response;
    for (const interceptor of this.responseInterceptors) {
      result = await interceptor(result);
    }
    return result;
  }

  /**
   * Execute all error interceptors
   */
  async executeErrorInterceptors(error: ApiError): Promise<ApiError> {
    let result: ApiError = error;
    for (const interceptor of this.errorInterceptors) {
      result = (await interceptor(result)) as ApiError;
    }
    return result;
  }
}








